import { useState, useEffect } from 'react';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, AlertCircle, Plus, Edit, FileText } from 'lucide-react';
import { confirmAsync } from '@/lib/confirm';

export function SepaMandate({ customerId }) {
  const [mandate, setMandate] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const [formData, setFormData] = useState({
    customer_iban: '',
    customer_bic: '',
    signature_date: '',
    sequence_type: 'RCUR'
  });

  useEffect(() => {
    fetchMandate();
  }, [customerId]);

  const fetchMandate = async () => {
    try {
      const response = await axiosInstance.get(`/sepa/mandates?customer_id=${customerId}`);
      if (response.data && response.data.length > 0) {
        setMandate(response.data[0]); // Get the first active mandate
      }
    } catch (error) {
      console.error('Error loading mandate:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    if (mandate) {
      setFormData({
        customer_iban: mandate.customer_iban,
        customer_bic: mandate.customer_bic,
        signature_date: new Date(mandate.signature_date).toISOString().split('T')[0],
        sequence_type: mandate.sequence_type
      });
    } else {
      setFormData({
        customer_iban: '',
        customer_bic: '',
        signature_date: new Date().toISOString().split('T')[0],
        sequence_type: 'RCUR'
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();

    try {
      await axiosInstance.post('/sepa/mandates', {
        customer_id: customerId,
        ...formData
      });

      toast.success('Mandato SEPA guardado correctamente');
      setDialogOpen(false);
      fetchMandate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar mandato');
    }
  };

  const handleUpdateStatus = async (status) => {
    if (!mandate) return;

    try {
      await axiosInstance.put(`/sepa/mandates/${mandate.id}/status?status=${status}`);
      toast.success('Estado actualizado correctamente');
      fetchMandate();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { label: 'Activo', class: 'bg-green-100 text-green-800', icon: CheckCircle2 },
      inactive: { label: 'Inactivo', class: 'bg-gray-100 text-gray-800', icon: XCircle },
      cancelled: { label: 'Cancelado', class: 'bg-red-100 text-red-800', icon: XCircle },
      expired: { label: 'Expirado', class: 'bg-orange-100 text-orange-800', icon: AlertCircle }
    };
    return config[status] || config.inactive;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-gray-500">
          Cargando...
        </CardContent>
      </Card>
    );
  }

  if (!mandate) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Mandato SEPA</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-600 mb-4">
              Este cliente no tiene un mandato SEPA configurado
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Para poder cobrar facturas mediante domiciliación bancaria, necesitas crear un mandato SEPA con los datos bancarios del cliente.
            </p>
            <Button onClick={handleOpenDialog} className="bg-[#9C0F17] hover:bg-[#7a0c12]">
              <Plus className="w-4 h-4 mr-2" />
              Crear Mandato SEPA
            </Button>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nuevo Mandato SEPA</DialogTitle>
                <DialogDescription>
                  Introduce los datos bancarios del cliente para la domiciliación
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSave} className="space-y-4">
                <div>
                  <Label htmlFor="customer_iban">IBAN del Cliente *</Label>
                  <Input
                    id="customer_iban"
                    value={formData.customer_iban}
                    onChange={(e) => setFormData({ ...formData, customer_iban: e.target.value })}
                    placeholder="ES12 3456 7890 1234 5678 9012"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="customer_bic">BIC del Banco *</Label>
                  <Input
                    id="customer_bic"
                    value={formData.customer_bic}
                    onChange={(e) => setFormData({ ...formData, customer_bic: e.target.value })}
                    placeholder="SANTESMMXXX"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="signature_date">Fecha de Firma del Mandato *</Label>
                  <Input
                    id="signature_date"
                    type="date"
                    value={formData.signature_date}
                    onChange={(e) => setFormData({ ...formData, signature_date: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="sequence_type">Tipo de Secuencia</Label>
                  <Select
                    value={formData.sequence_type}
                    onValueChange={(value) => setFormData({ ...formData, sequence_type: value })}
                  >
                    <SelectTrigger id="sequence_type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FRST">FRST - Primer adeudo</SelectItem>
                      <SelectItem value="RCUR">RCUR - Recurrente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2 justify-end pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-[#9C0F17] hover:bg-[#7a0c12] text-white">
                    Guardar Mandato
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    );
  }

  const statusBadge = getStatusBadge(mandate.status);
  const StatusIcon = statusBadge.icon;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Mandato SEPA</CardTitle>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleOpenDialog}>
            <Edit className="w-4 h-4 mr-1" />
            Editar
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <Badge className={statusBadge.class}>
            <StatusIcon className="w-3 h-3 mr-1" />
            {statusBadge.label}
          </Badge>
          <span className="text-sm text-gray-600">
            ID: <span className="font-mono">{mandate.mandate_id}</span>
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-gray-600">IBAN</Label>
            <p className="font-mono font-semibold">{mandate.customer_iban}</p>
          </div>

          <div>
            <Label className="text-gray-600">BIC</Label>
            <p className="font-mono font-semibold">{mandate.customer_bic}</p>
          </div>

          <div>
            <Label className="text-gray-600">Fecha de Firma</Label>
            <p className="font-semibold">
              {new Date(mandate.signature_date).toLocaleDateString()}
            </p>
          </div>

          <div>
            <Label className="text-gray-600">Tipo de Secuencia</Label>
            <p className="font-semibold">
              {mandate.sequence_type === 'FRST' ? 'Primer adeudo (FRST)' : 'Recurrente (RCUR)'}
            </p>
          </div>

          <div>
            <Label className="text-gray-600">Creado</Label>
            <p className="font-semibold">
              {new Date(mandate.created_at).toLocaleDateString()}
            </p>
          </div>

          <div>
            <Label className="text-gray-600">Última actualización</Label>
            <p className="font-semibold">
              {new Date(mandate.updated_at).toLocaleDateString()}
            </p>
          </div>
        </div>

        {mandate.status === 'active' && (
          <div className="flex gap-2 pt-4 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleUpdateStatus('inactive')}
            >
              Desactivar Mandato
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={async () => {
                if (await confirmAsync('¿Estás seguro de cancelar este mandato?')) {
                  handleUpdateStatus('cancelled');
                }
              }}
            >
              Cancelar Mandato
            </Button>
          </div>
        )}

        {mandate.status === 'inactive' && (
          <div className="flex gap-2 pt-4 border-t">
            <Button
              variant="default"
              size="sm"
              onClick={() => handleUpdateStatus('active')}
            >
              Reactivar Mandato
            </Button>
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Editar Mandato SEPA</DialogTitle>
              <DialogDescription>
                Actualiza los datos bancarios del cliente
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <Label htmlFor="customer_iban">IBAN del Cliente *</Label>
                <Input
                  id="customer_iban"
                  value={formData.customer_iban}
                  onChange={(e) => setFormData({ ...formData, customer_iban: e.target.value })}
                  placeholder="ES12 3456 7890 1234 5678 9012"
                  required
                />
              </div>

              <div>
                <Label htmlFor="customer_bic">BIC del Banco *</Label>
                <Input
                  id="customer_bic"
                  value={formData.customer_bic}
                  onChange={(e) => setFormData({ ...formData, customer_bic: e.target.value })}
                  placeholder="SANTESMMXXX"
                  required
                />
              </div>

              <div>
                <Label htmlFor="signature_date">Fecha de Firma del Mandato *</Label>
                <Input
                  id="signature_date"
                  type="date"
                  value={formData.signature_date}
                  onChange={(e) => setFormData({ ...formData, signature_date: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="sequence_type">Tipo de Secuencia</Label>
                <Select
                  value={formData.sequence_type}
                  onValueChange={(value) => setFormData({ ...formData, sequence_type: value })}
                >
                  <SelectTrigger id="sequence_type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FRST">FRST - Primer adeudo</SelectItem>
                    <SelectItem value="RCUR">RCUR - Recurrente</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-[#9C0F17] hover:bg-[#7a0c12] text-white">
                  Guardar Cambios
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
